<template>
  <SurveyStep />

  <div class="vf-mt-7">
    <p class="vf-text-lg">{{ currentQuestion?.question }}</p>
    <p class="text-sm vf-text-red-600 vf-mt-2" v-if="errSubForm">
      *{{ errSubForm }}
    </p>
    <p
      class="text-muted text-sm vf-mb-2 vf-mt-3 vf-italic"
      v-if="currentQuestion?.answer_type == 'Multiple'"
    >
      Choose one or more that are most relevant :
    </p>

    <div class="vf-flex vf-flex-wrap vf-gap-x-2 vf-mt-2 vf-gap-y-2">
      <template
        v-for="(option, keyChild) in currentQuestion?.options"
        :key="keyChild"
      >
        <div
          class="vf-px-3 vf-py-2 vf-rounded-full vf-border vf-border-[#E0E0E0] vf-cursor-pointer vf-transition"
          @click="onChangeAnswer(currentQuestion?.id, option.id)"
          :class="
            isAnswerSelected(option.id)
              ? 'vf-border-primary-100 vf-bg-primary-50 vf-text-primary-500'
              : 'hover:vf-border-primary-100 hover:vf-bg-primary-50 hover:vf-text-primary-500'
          "
        >
          {{ option.option_label }}
        </div>
      </template>
    </div>

    <template v-if="childrenAnswers.length > 0">
      <div class="vf-flex vf-flex-wrap vf-gap-x-2 vf-mt-2 vf-gap-y-2">
        <template
          v-for="(option, keyChildAnswer) in childrenAnswers"
          :key="keyChildAnswer"
        >
          <div
            class="vf-px-3 vf-py-2 vf-rounded-full vf-border vf-border-[#E0E0E0] vf-cursor-pointer vf-transition"
            @click="
              onChangeChildAnswer(
                currentQuestion?.id,
                currentAnswer[0],
                option.id
              )
            "
            :class="
              isChildAnswerSelected(option.id)
                ? 'vf-border-primary-100 vf-bg-primary-50 vf-text-primary-500'
                : 'hover:vf-border-primary-100 hover:vf-bg-primary-50 hover:vf-text-primary-500'
            "
          >
            {{ option.option_label }}
          </div>
        </template>
      </div>
    </template>

    <Field
      type="text"
      class="form-control vf-mt-6"
      id="other_answer"
      placeholder="Type your answer"
      name="other_answer"
      v-model="answers[surveyState.value?.surveyStep ?? 0].other_answer"
      v-if="isOtherAnswer"
    />

    <div class="vf-mt-10 vf-flex vf-justify-end vf-gap-x-4 vf-items-center">
      <a
        href="javascript:;"
        class="!vf-underline-none !vf-text-[#757575]"
        v-if="!currentQuestion?.is_mandatory"
      >
        Skip
      </a>
      <base-button
        :theme="'secondary'"
        type="button"
        @click="onContinueClick"
        :loading="isLoading"
      >
        Continue
      </base-button>
    </div>
  </div>
</template>

<script setup>
import SurveyStep from "@/views/dashboard/components/onboarding/SurveyStep.vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { httpClientAuth } from "@/libraries";
import { ref, computed, getCurrentInstance } from "vue";
import { Field } from "vee-validate";

const emitt = defineEmits(["onStateChange"]);
const queryClient = useQueryClient();

const questions = ref([]);
useQuery({
  queryKey: ["QUERY_GET_QUESTIONS"],
  queryFn: async () => {
    const response = await httpClientAuth.get(
      "/v4/onboarding-survey/questions"
    );

    questions.value = response.data.data;
    return response.data.data;
  },
});

const { data: surveyState } = useQuery({
  queryKey: ["QUERY_SURVEY_STATE"],
  queryFn: () => ({
    surveyStep: 1,
  }),
  staleTime: Infinity,
});

const updateSurveyState = useMutation({
  mutationFn: (newState) => {
    queryClient.setQueryData(["QUERY_SURVEY_STATE"], (oldState) => {
      return {
        ...oldState,
        ...newState,
      };
    });
    return newState;
  },
});

const answers = ref([]);
const errSubForm = ref("");
const isLoading = ref(false);

const currentQuestion = computed(() => {
  return questions.value[(surveyState.value?.surveyStep ?? 0) - 1];
});

const currentAnswer = computed(() => {
  const selectedOption = answers.value.find(
    (item) => item.question_id == currentQuestion.value.id
  );

  return selectedOption ? selectedOption.answer_id : [];
});

const isOtherAnswer = computed(() => {
  const selectedOption = answers.value.find(
    (item) => item.question_id == currentQuestion.value.id
  );

  return selectedOption ? selectedOption.is_other : false;
});

const childrenAnswers = computed(() => {
  const selectedOption = answers.value.find(
    (item) => item.question_id == currentQuestion.value.id
  );

  const isChildren = selectedOption ? selectedOption.is_children : false;
  return isChildren ? selectedOption.children_options : [];
});

const isAnswerSelected = (answerId) => {
  return currentAnswer.value.indexOf(answerId) >= 0;
};

const isChildAnswerSelected = (childAnswerId) => {
  const selectedOption = answers.value.find(
    (item) => item.question_id == currentQuestion.value.id
  );

  return selectedOption.children_answer_id == childAnswerId;
};

const { proxy } = getCurrentInstance();

const isValidateSubForm = () => {
  const selectedAnswer = answers.value.find(
    (item) => item.question_id == currentQuestion.value.id
  );

  return (
    !currentQuestion.value.is_mandatory ||
    (currentQuestion.value.is_mandatory &&
      ((selectedAnswer.is_other &&
        selectedAnswer.other_answer.trim().length > 0) ||
        (!selectedAnswer.is_other && currentAnswer.value.length > 0)))
  );
};

const mutationSubmitSurvey = useMutation({
  mutationFn: async ({ answerParams }) => {
    const response = await httpClientAuth.post("/v4/onboarding-survey/answer", {
      answers: answerParams,
    });
    return response;
  },
  onSuccess: async (res) => {
    const { success, message } = res.data;
    if (success) {
      window.localStorage.removeItem("_survey_onboard");
      emitt("onStateChange");
    } else {
      proxy.errorToast(message);
    }

    isLoading.value = false;
  },
  onError: (e) => {
    console.error(e);
    isLoading.value = false;
    proxy.errorToast("Sorry registration failed, something went wrong");
  },
});

const onContinueClick = () => {
  if (!isValidateSubForm()) {
    errSubForm.value =
      "You must select at least one option from the given answer choices.";
    return;
  }

  if (surveyState.value.surveyStep >= questions.value.length) {
    // submit form
    const params = [];
    answers.value.forEach((item) => {
      const selectedQuestion = questions.value.find(
        (itemQuestion) => itemQuestion.id == item.question_id
      );

      if (selectedQuestion.answer_type == "Multiple") {
        item.answer_id.forEach((itemAnswer) => {
          const selectedOption = selectedQuestion.options.find(
            (itemOption) => itemOption.id == itemAnswer
          );

          params.push({
            question_id: item.question_id,
            option_selected_label: item.is_other
              ? item.other_answer
              : selectedOption.option_label,
            option_is_other: item.is_other,
            is_skip: false,
          });
        });
      } else {
        let selectedOption;
        if (item.children_answer_id) {
          const parentOption = selectedQuestion.options.find(
            (itemOption) => itemOption.id == item.answer_id[0]
          );

          selectedOption = parentOption.childrens.find(
            (itemOption) => itemOption.parent_option_id == parentOption.id
          );
        } else {
          selectedOption = selectedQuestion.options.find(
            (itemOption) => itemOption.id == item.answer_id[0]
          );
        }

        params.push({
          question_id: item.question_id,
          option_selected_label: item.is_other
            ? item.other_answer
            : selectedOption.option_label,
          option_is_other: item.is_other,
          is_skip: false,
        });
      }
    });

    isLoading.value = true;
    mutationSubmitSurvey.mutate({
      answerParams: params,
    });
  } else {
    updateSurveyState.mutate({
      surveyStep: surveyState.value.surveyStep + 1,
    });
  }
};

const onChangeChildAnswer = (questionId, answerId, childAnswerId) => {
  const selectedAnswerIndex = answers.value.findIndex(
    (item) => item.question_id == questionId
  );

  answers.value[selectedAnswerIndex]["children_answer_id"] = childAnswerId;
};

const onChangeAnswer = (questionId, answerId) => {
  const selectedAnswerIndex = answers.value.findIndex(
    (item) => item.question_id == questionId
  );

  const selectedOption = currentQuestion.value.options.find(
    (item) => item.id == answerId
  );

  const isOther =
    selectedOption.option_label.trim().match(/^other$/gi) !== null;

  if (selectedAnswerIndex < 0) {
    answers.value.push({
      question_id: questionId,
      answer_id: [answerId],
      is_multiple: currentQuestion.value.answer_type == "Multiple",
      is_other: isOther,
      is_children: selectedOption.childrens.length > 0,
      children_options: selectedOption.childrens,
      children_answer_id: null,
      other_answer: "",
    });
  } else {
    if (currentQuestion.value.answer_type == "Multiple") {
      const selectedAnswer = answers.value[selectedAnswerIndex];
      const isSelectedOption = selectedAnswer.answer_id.indexOf(answerId) >= 0;

      if (isSelectedOption) {
        answers.value[selectedAnswerIndex]["answer_id"].splice(
          currentAnswer.value.indexOf(answerId),
          1
        );
      } else {
        if (isOther) {
          answers.value[selectedAnswerIndex]["answer_id"] = [answerId];
        } else {
          if (selectedAnswer.is_other) {
            answers.value[selectedAnswerIndex]["answer_id"] = [answerId];
          } else {
            answers.value[selectedAnswerIndex]["answer_id"].push(answerId);
          }
        }

        answers.value[selectedAnswerIndex].is_other = isOther;
      }
    } else {
      answers.value[selectedAnswerIndex]["is_other"] = isOther;
      answers.value[selectedAnswerIndex]["is_children"] =
        selectedOption.childrens.length > 0;
      answers.value[selectedAnswerIndex]["children_options"] =
        selectedOption.childrens;
      answers.value[selectedAnswerIndex]["children_answer_id"] = null;
      answers.value[selectedAnswerIndex]["answer_id"] = [answerId];
    }
  }

  if (isValidateSubForm) {
    errSubForm.value = "";
  }
};
</script>
